import React from 'react';
import { X, Filter, ArrowUpDown } from 'lucide-react';
import type { ChronologyQueryResponse } from '../services/chronologyApi';

interface ChronologyQueryResultsProps {
  result: ChronologyQueryResponse;
  totalEventCount: number;
  onClear: () => void;
}

const INTENT_CONFIG = {
  FILTER: { icon: Filter, label: 'Filtered', colour: 'bg-cyan-50 dark:bg-cyan-900/20 border-cyan-200 dark:border-cyan-800' },
  SORT: { icon: ArrowUpDown, label: 'Sorted', colour: 'bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800' },
} as const;

export const ChronologyQueryResults: React.FC<ChronologyQueryResultsProps> = ({
  result,
  totalEventCount,
  onClear,
}) => {
  const config = INTENT_CONFIG[result.intent as keyof typeof INTENT_CONFIG];
  if (!config) return null;

  const Icon = config.icon;

  return (
    <div className={`mb-4 px-4 py-3 border rounded-lg ${config.colour}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2.5 min-w-0">
          <Icon className="h-4 w-4 mt-0.5 flex-shrink-0 text-medical-600 dark:text-medical-400" />
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-medium text-medical-900 dark:text-medical-100">
                {config.label}
              </span>
              {result.intent === 'FILTER' && (
                <span className="text-sm text-medical-600 dark:text-medical-400">
                  — showing {result.eventIds.length} of {totalEventCount} events
                </span>
              )}
              {result.intent === 'SORT' && result.sortDescription && (
                <span className="text-sm text-medical-600 dark:text-medical-400">
                  — {result.sortDescription}
                </span>
              )}
            </div>

            {result.appliedFilters && result.intent === 'FILTER' && (
              <p className="text-xs text-medical-500 dark:text-medical-400 mt-0.5">
                {result.appliedFilters}
              </p>
            )}
          </div>
        </div>

        <button
          onClick={onClear}
          className="flex-shrink-0 p-1 rounded-md hover:bg-medical-200 dark:hover:bg-medical-700 text-medical-500 dark:text-medical-400 transition-colors"
          title="Clear filter"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};
